import type { SampleStyleOptionData } from '../../types';

import { randBoolean, randColor, randNumber } from '@ngneat/falso';

import { mockSampleStyleEan } from './mockSampleStyleEan';

const colourNames = [
    'Black',
    'Dark Navy',
    'Dark Grey Melange',
    'Bungee Cord',
    'Outer Space',
    'White',
    'Estate Blue',
    'Night Sky',
];

const defaultSizeRun = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

// Denim-style run where every size carries multiple lengths (e.g. waist x leg).
const lengthSizeRun = ['27', '28', '29', '30', '31', '32', '34', '36', '38'];
const lengthRun = ['30', '32', '34', '36'];

interface MockSampleStyleOptionDataConfig {
    /** Build a size run where every size has multiple lengths (e.g. denim) */
    withLengths?: boolean;
}

const mockSize = (
    name: string
): SampleStyleOptionData['styleEans'][number]['size'] => ({
    styleAttributeNumber: String(randNumber({ min: 1000, max: 9999 })),
    styleAttributeName: name,
});

const mockLength = (
    name: string
): NonNullable<SampleStyleOptionData['styleEans'][number]['length']> => ({
    styleAttributeNumber: String(randNumber({ min: 1000, max: 9999 })),
    styleAttributeName: name,
});

const buildStyleEans = (
    withLengths: boolean
): SampleStyleOptionData['styleEans'] =>
    withLengths
        ? lengthSizeRun.flatMap((size) =>
              lengthRun.map((length) =>
                  mockSampleStyleEan({
                      size: mockSize(size),
                      length: mockLength(length),
                  })
              )
          )
        : defaultSizeRun.map((size) =>
              mockSampleStyleEan({ size: mockSize(size) })
          );

/**
 * Returns a mocked SampleStyleOptionData (as returned by `getStyleEanForStyle`)
 * with an EAN per size in the default run. Pass `{ withLengths: true }` to build
 * a denim-style run of size x length.
 */
const mockSampleStyleOptionData = (
    overwrite?: Partial<SampleStyleOptionData>,
    { withLengths = false }: MockSampleStyleOptionDataConfig = {}
): SampleStyleOptionData => {
    const colourName =
        colourNames[randNumber({ min: 0, max: colourNames.length - 1 })];

    return {
        styleOptionNumber: randNumber({ min: 4000000, max: 5999999 }),
        colourName,
        hexCode: randColor(),
        styleVariant: randBoolean() ? `Pack:${colourName}` : null,
        styleEans: buildStyleEans(withLengths),
        ...overwrite,
    };
};

export { mockSampleStyleOptionData };
